import { UcoastEl } from '@/scripts/core/UcoastEl'
import { TsDOM as q } from '@/scripts/core/TsDOM'

export class MenuShowcase extends UcoastEl {
	static htmlSelector = 'menu-showcase'

	private tabs: HTMLButtonElement[] = []
	private items: HTMLElement[] = []
	private tiles: HTMLButtonElement[] = []
	private stageImages: HTMLElement[] = []
	private stacks: HTMLElement[] = []
	private detailToggles: HTMLButtonElement[] = []
	private detailPanel?: HTMLElement
	private slides: HTMLElement[] = []
	private dots: HTMLButtonElement[] = []
	private track?: HTMLElement
	private currentHandle?: string
	private mql?: MediaQueryList
	private rafId = 0
	// Seamless loop: clones of the first/last visible slide bracket the track so a
	// swipe past either end lands on a clone, then we jump (no animation) to the
	// matching real slide. Rebuilt whenever the visible (category-filtered) set changes.
	private clones: HTMLElement[] = []
	private headClone?: HTMLElement
	private tailClone?: HTMLElement
	private firstReal?: HTMLElement
	private lastReal?: HTMLElement
	private loopEnabled = false
	private settleTimer = 0
	private onKeydown?: (e: KeyboardEvent) => void
	private onDocClick?: (e: MouseEvent) => void
	private onFloatingNavOpen?: () => void
	private onMqlChange?: () => void
	private onTrackScroll?: () => void
	private proteinTween?: { card: HTMLElement; done: () => void }

	override connectedCallback() {
		super.connectedCallback()

		this.tabs = Array.from(q.ol<HTMLButtonElement>('[data-menu-tab]', this) ?? [])
		this.items = Array.from(q.ol<HTMLElement>('[data-menu-item]', this) ?? [])
		this.tiles = Array.from(q.ol<HTMLButtonElement>('[data-menu-item-trigger]', this) ?? [])
		this.stageImages = Array.from(q.ol<HTMLElement>('[data-menu-stage-image]', this) ?? [])
		this.stacks = Array.from(q.ol<HTMLElement>('[data-menu-stack]', this) ?? [])
		this.detailToggles = Array.from(q.ol<HTMLButtonElement>('[data-menu-detail-toggle]', this) ?? [])
		this.detailPanel = q.os<HTMLElement>('[data-menu-detail]', this)
		this.slides = Array.from(q.ol<HTMLElement>('[data-menu-slide]', this) ?? [])
		this.dots = Array.from(q.ol<HTMLButtonElement>('[data-menu-dot]', this) ?? [])
		this.track = q.os<HTMLElement>('[data-menu-track]', this)

		this.bindTabs()
		this.bindTiles()
		this.bindDots()
		this.bindDetail()
		this.bindProteins()
		this.bindDismiss()

		const firstActive = this.tiles.find((t) => t.getAttribute('aria-pressed') === 'true')
		if (firstActive) {
			firstActive.classList.add('is-active')
			const handle = this.handleFromTile(firstActive)
			if (handle) {
				this.currentHandle = handle
				this.markStageImage(handle, true)
			}
		}

		// The slider only exists on mobile; wire/unwire it on breakpoint change.
		this.mql = window.matchMedia('(max-width: 749px)')
		this.onMqlChange = () => this.handleMediaChange()
		this.mql.addEventListener('change', this.onMqlChange)
		this.handleMediaChange()
	}

	override disconnectedCallback() {
		super.disconnectedCallback()
		if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown)
		if (this.onDocClick) document.removeEventListener('click', this.onDocClick)
		if (this.onFloatingNavOpen) document.removeEventListener('floating-nav:open', this.onFloatingNavOpen)
		if (this.mql && this.onMqlChange) this.mql.removeEventListener('change', this.onMqlChange)
		this.teardownSlider()
	}

	private get isMobile(): boolean {
		return !!this.mql?.matches
	}

	private handleFromTile(tile: HTMLElement): string | undefined {
		const li = tile.closest<HTMLElement>('[data-menu-item]')
		return li?.dataset.menuItem
	}

	private bindTabs() {
		this.tabs.forEach((tab) => {
			tab.addEventListener('click', () => {
				const cat = tab.dataset.menuTab
				if (!cat) return
				this.setCategory(cat)
			})
		})
	}

	private bindTiles() {
		this.tiles.forEach((tile) => {
			tile.addEventListener('click', () => {
				const handle = this.handleFromTile(tile)
				if (!handle) return
				this.activateItem(handle)
			})
		})
	}

	private bindDots() {
		this.dots.forEach((dot) => {
			dot.addEventListener('click', () => {
				const handle = dot.dataset.menuDot
				if (handle) this.scrollToHandle(handle, 'smooth')
			})
		})
	}

	// --- Protein toggle ------------------------------------------------------
	// A grouped card keeps one source recipe per protein, so each option swaps a
	// whole pane (ingredients, macros, description) and the order link with it.
	private bindProteins() {
		const buttons = Array.from(q.ol<HTMLButtonElement>('[data-menu-protein]', this) ?? [])
		buttons.forEach((btn) => {
			btn.addEventListener('click', () => this.setProtein(btn))
		})
	}

	private setProtein(btn: HTMLButtonElement) {
		const panel = btn.closest<HTMLElement>('[data-menu-stack]')
		const handle = btn.dataset.menuProtein
		if (!panel || !handle) return

		Array.from(q.ol<HTMLButtonElement>('[data-menu-protein]', panel) ?? []).forEach((other) => {
			other.setAttribute('aria-pressed', String(other === btn))
		})

		const card = q.os<HTMLElement>('.menu-showcase__nutrition-content', panel)
		const body = q.os<HTMLElement>('[data-menu-nutrition-body]', panel)
		const open = !!body && !body.hidden
		const from = card && open ? card.getBoundingClientRect().height : 0

		Array.from(q.ol<HTMLElement>('[data-menu-protein-pane]', panel) ?? []).forEach((pane) => {
			const match = pane.dataset.menuProteinPane === handle
			pane.hidden = !match
			pane.toggleAttribute('inert', !match)
		})

		if (card && open) this.tweenCardHeight(card, from)

		Array.from(q.ol<HTMLElement>('[data-menu-protein-intro]', panel) ?? []).forEach((intro) => {
			intro.hidden = intro.dataset.menuProteinIntro !== handle
		})

		// Keep the order button pointed at this protein's own ASP link.
		const order = q.os<HTMLAnchorElement>('[data-menu-order]', panel)
		const url = btn.dataset.menuProteinOrder
		if (order && url) order.href = url

		this.markProteinImage(panel, handle)
	}

	// Options differ by one ingredient row, so the card jumps ~40px on switch.
	// Ease that height instead of snapping it; the content itself paints at once.
	private tweenCardHeight(card: HTMLElement, from: number) {
		this.proteinTween?.done()
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

		const to = card.getBoundingClientRect().height
		if (Math.abs(to - from) < 1) return

		const done = () => {
			card.style.height = ''
			card.style.overflow = ''
			card.style.transition = ''
			card.removeEventListener('transitionend', onEnd)
			this.proteinTween = undefined
		}
		const onEnd = (e: TransitionEvent) => {
			if (e.target === card && e.propertyName === 'height') done()
		}

		card.style.height = `${from}px`
		card.style.overflow = 'hidden'
		card.style.transition = 'none'
		void card.offsetHeight // flush the start height before transitioning
		card.style.transition = 'height 200ms var(--ease-out-slow)'
		card.style.height = `${to}px`
		card.addEventListener('transitionend', onEnd)
		this.proteinTween = { card, done }
	}

	// Stage images are per item; a grouped item also carries one image per recipe.
	private markProteinImage(panel: HTMLElement, handle: string) {
		const itemId = panel.dataset.menuStack
		if (!itemId) return
		const wrap = this.stageImages.find((el) => el.dataset.menuStageImage === itemId)
		if (!wrap) return
		const variants = Array.from(q.ol<HTMLElement>('[data-menu-protein-image]', wrap) ?? [])
		if (!variants.length) return
		variants.forEach((image) => {
			const match = image.dataset.menuProteinImage === handle
			image.toggleAttribute('data-active', match)
			image.setAttribute('aria-hidden', String(!match))
		})
	}

	private bindDetail() {
		this.detailToggles.forEach((btn) => {
			btn.addEventListener('click', () => this.toggleDetail())
		})

		this.onKeydown = (e: KeyboardEvent) => {
			if (e.key === 'Escape' && this.dataset.detailShow === 'true') {
				this.setDetailOpen(false)
				this.activeToggle()?.focus()
			}
		}
		document.addEventListener('keydown', this.onKeydown)
	}

	private bindDismiss() {
		this.onDocClick = (e: MouseEvent) => {
			if (this.dataset.detailShow !== 'true') return
			const target = e.target as Node | null
			if (!target || !this.detailPanel) return
			// Choosing another item keeps the panel; only a click away closes it.
			const el = target instanceof Element ? target : target.parentElement
			if (el?.closest('[data-menu-item-trigger], [data-menu-dot]')) return
			if (!this.detailPanel.contains(target)) this.setDetailOpen(false)
		}
		this.onFloatingNavOpen = () => {
			if (this.dataset.detailShow === 'true') this.setDetailOpen(false)
		}
		document.addEventListener('click', this.onDocClick)
		document.addEventListener('floating-nav:open', this.onFloatingNavOpen)
	}

	private activeToggle(): HTMLButtonElement | undefined {
		// Toggle in the currently visible stack (the one with data-active='true').
		const activeStack = this.stacks.find((s) => s.dataset.active === 'true')
		if (!activeStack) return this.detailToggles[0]
		return q.os<HTMLButtonElement>('[data-menu-detail-toggle]', activeStack) ?? this.detailToggles[0]
	}

	private toggleDetail() {
		this.setDetailOpen(this.dataset.detailShow !== 'true')
	}

	private setDetailOpen(open: boolean) {
		const expanded = open ? 'true' : 'false'
		this.detailToggles.forEach((btn) => {
			btn.setAttribute('aria-expanded', expanded)
			btn.querySelector('.menu-showcase__nutrition-toggle-label--closed')?.setAttribute('aria-hidden', String(open))
			btn.querySelector('.menu-showcase__nutrition-toggle-label--open')?.setAttribute('aria-hidden', String(!open))
		})
		this.querySelectorAll<HTMLElement>('[data-menu-nutrition-body]').forEach((body) => {
			body.hidden = !open
			body.toggleAttribute('inert', !open)
		})
		if (open) {
			this.dataset.detailShow = 'true'
		} else {
			delete this.dataset.detailShow
		}
	}

	private setCategory(category: string) {
		if (this.dataset.activeCategory === category) return
		this.dataset.activeCategory = category
		this.setDetailOpen(false)

		this.tabs.forEach((tab) => {
			tab.setAttribute('aria-selected', String(tab.dataset.menuTab === category))
		})

		let firstVisible: HTMLElement | undefined
		this.items.forEach((item) => {
			const match = item.dataset.menuItemCategory === category
			if (match) {
				item.removeAttribute('hidden')
				if (!firstVisible) firstVisible = item
			} else {
				item.setAttribute('hidden', '')
			}
		})

		// Filter the mobile slider track + dots to the active category.
		this.slides.forEach((slide) => {
			slide.toggleAttribute('hidden', slide.dataset.menuItemCategory !== category)
		})
		this.dots.forEach((dot) => {
			dot.toggleAttribute('hidden', dot.dataset.menuItemCategory !== category)
		})

		const activeHandle = this.activeHandle()
		const activeStillVisible = activeHandle && this.items.find(
			(i) => i.dataset.menuItem === activeHandle && !i.hasAttribute('hidden')
		)
		if (!activeStillVisible && firstVisible) {
			const handle = firstVisible.dataset.menuItem
			if (handle) this.activateItem(handle)
		}

		// Re-centre the slider on the first card of the new category.
		if (this.isMobile && this.track) {
			// Rebuild the loop clones around the new visible set before centring.
			this.buildLoop()
			const firstSlide = this.slides.find((s) => !s.hasAttribute('hidden'))
			const handle = firstSlide?.dataset.menuSlide
			if (handle) {
				this.scrollToHandle(handle, 'auto')
				this.setCurrentSlide(handle)
			}
		}
	}

	private activeHandle(): string | undefined {
		const pressed = this.tiles.find((t) => t.getAttribute('aria-pressed') === 'true')
		return pressed ? this.handleFromTile(pressed) : undefined
	}

	private activateItem(handle: string, opts?: { updateImages?: boolean }) {
		this.currentHandle = handle
		// On mobile every image is a visible slide, so leave the crossfade state
		// alone (marking non-active images `hidden` would blank out the slider).
		const updateImages = opts?.updateImages ?? (!this.isMobile || this.classList.contains('menu-showcase--nutrition'))

		// An open panel stays open across items: the reader asked for the detail,
		// and keeping it lets them compare one salad against the next.
		const keepOpen = this.dataset.detailShow === 'true'
		const fromHeight = keepOpen ? this.activeCard()?.getBoundingClientRect().height ?? 0 : 0
		if (!keepOpen) this.setDetailOpen(false)

		this.tiles.forEach((tile) => {
			const match = this.handleFromTile(tile) === handle
			tile.setAttribute('aria-pressed', String(match))
			tile.classList.toggle('is-active', match)
		})

		if (updateImages) {
			this.stageImages.forEach((img) => {
				this.markStageImage(img.dataset.menuStageImage ?? '', img.dataset.menuStageImage === handle)
			})
		}

		this.stacks.forEach((stack) => {
			const match = stack.dataset.menuStack === handle
			if (match) {
				stack.dataset.active = 'true'
				stack.setAttribute('aria-hidden', 'false')
				stack.removeAttribute('inert')
			} else {
				delete stack.dataset.active
				stack.setAttribute('aria-hidden', 'true')
				stack.setAttribute('inert', '')
			}
		})

		if (keepOpen) {
			this.setDetailOpen(true)
			const card = this.activeCard()
			if (card) {
				this.fadeInCard(card)
				this.tweenCardHeight(card, fromHeight)
			}
		}
	}

	// The open card of the active item, i.e. the one the reader is looking at.
	private activeCard() {
		const panel = q.os<HTMLElement>('[data-menu-stack][data-active="true"]', this)
		return panel ? q.os<HTMLElement>('.menu-showcase__nutrition-content', panel) : undefined
	}

	// Cards are separate elements, so the new one fades up rather than crossfades.
	// Uses the animation API so it cannot fight the inline height transition.
	private fadeInCard(card: HTMLElement) {
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
		card.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 180, easing: 'ease-out' })
	}

	private markStageImage(handle: string, active: boolean) {
		const wrap = this.stageImages.find((s) => s.dataset.menuStageImage === handle)
		if (!wrap) return
		if (active) {
			wrap.removeAttribute('hidden')
			wrap.removeAttribute('aria-hidden')
			wrap.dataset.active = 'true'
		} else {
			wrap.setAttribute('hidden', '')
			wrap.setAttribute('aria-hidden', 'true')
			delete wrap.dataset.active
		}
	}

	// --- Mobile card slider -------------------------------------------------

	private handleMediaChange() {
		if (this.classList.contains('menu-showcase--nutrition')) return
		if (this.isMobile) {
			this.stageImages.forEach((image) => {
				image.removeAttribute('hidden')
				image.removeAttribute('aria-hidden')
			})
			this.setupSlider()
		} else {
			this.teardownSlider()
			// Re-sync the desktop crossfade to whatever the slider last centred.
			if (this.currentHandle) {
				const current = this.currentHandle
				this.stageImages.forEach((img) =>
					this.markStageImage(img.dataset.menuStageImage ?? '', img.dataset.menuStageImage === current)
				)
			}
		}
	}

	private setupSlider() {
		if (!this.track || this.onTrackScroll) return
		this.onTrackScroll = () => {
			if (!this.rafId) {
				this.rafId = requestAnimationFrame(() => {
					this.rafId = 0
					this.updateCurrentFromScroll()
				})
			}
			// After scrolling (incl. snap momentum) settles, wrap if we landed on a clone.
			if (this.loopEnabled) {
				if (this.settleTimer) clearTimeout(this.settleTimer)
				this.settleTimer = window.setTimeout(() => {
					this.settleTimer = 0
					this.checkLoopWrap()
				}, 140)
			}
		}
		this.track.addEventListener('scroll', this.onTrackScroll, { passive: true })

		this.buildLoop()

		// Centre on the current card, then sync class + dots to reality.
		if (this.currentHandle) this.scrollToHandle(this.currentHandle, 'auto')
		this.updateCurrentFromScroll()
	}

	private teardownSlider() {
		if (this.rafId) {
			cancelAnimationFrame(this.rafId)
			this.rafId = 0
		}
		if (this.track && this.onTrackScroll) {
			this.track.removeEventListener('scroll', this.onTrackScroll)
		}
		this.onTrackScroll = undefined
		this.destroyLoop()
		this.slides.forEach((s) => s.classList.remove('is-current'))
	}

	// All slides participating in scroll geometry — real slides plus loop clones.
	private scanSlides(): HTMLElement[] {
		return this.loopEnabled ? [...this.slides, ...this.clones] : this.slides
	}

	// Pick the slide (real or clone) whose centre is closest to the track centre.
	private nearestSlide(): HTMLElement | undefined {
		if (!this.track) return undefined
		const trackRect = this.track.getBoundingClientRect()
		const trackCenter = trackRect.left + trackRect.width / 2
		let best: HTMLElement | undefined
		let bestDist = Infinity
		this.scanSlides().forEach((slide) => {
			if (slide.hasAttribute('hidden')) return
			const r = slide.getBoundingClientRect()
			const dist = Math.abs(r.left + r.width / 2 - trackCenter)
			if (dist < bestDist) {
				bestDist = dist
				best = slide
			}
		})
		return best
	}

	private updateCurrentFromScroll() {
		const handle = this.nearestSlide()?.dataset.menuSlide
		if (handle) this.setCurrentSlide(handle)
	}

	// --- Seamless loop ------------------------------------------------------

	// Bracket the visible set with a clone of the last slide (prepended) and a
	// clone of the first (appended), so there's scroll room past both ends.
	private buildLoop() {
		this.destroyLoop()
		if (!this.track) return
		const visible = this.slides.filter((s) => !s.hasAttribute('hidden'))
		if (visible.length < 2) return

		this.firstReal = visible[0]
		this.lastReal = visible[visible.length - 1]
		this.headClone = this.makeClone(this.lastReal)
		this.tailClone = this.makeClone(this.firstReal)
		this.track.insertBefore(this.headClone, this.track.firstChild)
		this.track.appendChild(this.tailClone)
		this.clones = [this.headClone, this.tailClone]
		this.loopEnabled = true
	}

	private makeClone(node: HTMLElement): HTMLElement {
		const clone = node.cloneNode(true) as HTMLElement
		clone.dataset.menuClone = 'true'
		clone.setAttribute('aria-hidden', 'true')
		clone.setAttribute('inert', '')
		clone.removeAttribute('id')
		return clone
	}

	private destroyLoop() {
		this.clones.forEach((c) => c.remove())
		this.clones = []
		this.headClone = undefined
		this.tailClone = undefined
		this.firstReal = undefined
		this.lastReal = undefined
		this.loopEnabled = false
		if (this.settleTimer) {
			clearTimeout(this.settleTimer)
			this.settleTimer = 0
		}
	}

	// If scrolling settled on a clone, jump (no animation) to the real equivalent.
	private checkLoopWrap() {
		if (!this.track || !this.loopEnabled) return
		if (!this.headClone || !this.tailClone || !this.firstReal || !this.lastReal) return
		const nearest = this.nearestSlide()
		if (nearest === this.headClone) {
			this.jumpToReal(this.lastReal, this.headClone)
		} else if (nearest === this.tailClone) {
			this.jumpToReal(this.firstReal, this.tailClone)
		}
	}

	private jumpToReal(real: HTMLElement, clone: HTMLElement) {
		if (!this.track) return
		// Shift scroll so the real slide sits exactly where its clone is now —
		// the two are pixel-identical, so the reposition is invisible.
		const delta = real.getBoundingClientRect().left - clone.getBoundingClientRect().left
		this.track.scrollLeft += delta
		const handle = real.dataset.menuSlide
		if (handle) this.setCurrentSlide(handle)
	}

	private setCurrentSlide(handle: string) {
		this.markCurrentSlide(handle)
		this.updateDots(handle)
		if (handle !== this.currentHandle) {
			this.currentHandle = handle
			// Sync the detail stacks (and tiles) without touching the crossfade.
			this.activateItem(handle, { updateImages: false })
		}
	}

	private markCurrentSlide(handle: string) {
		this.scanSlides().forEach((slide) => {
			slide.classList.toggle('is-current', slide.dataset.menuSlide === handle)
		})
	}

	private updateDots(handle: string) {
		this.dots.forEach((dot) => {
			dot.classList.toggle('is-active', dot.dataset.menuDot === handle)
		})
	}

	private scrollToHandle(handle: string, behavior: ScrollBehavior) {
		if (!this.track) return
		const slide = this.slides.find((s) => s.dataset.menuSlide === handle)
		if (!slide) return
		const trackRect = this.track.getBoundingClientRect()
		const slideRect = slide.getBoundingClientRect()
		const delta = slideRect.left + slideRect.width / 2 - (trackRect.left + trackRect.width / 2)
		this.track.scrollBy({ left: delta, behavior })
	}
}
